import React, { useContext, useEffect, useState } from 'react'

import { AcornsIcon } from '@acorns/icons'
import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'

import { CTAContainer } from 'components/CTAContainer'
import Layout from 'components/Layout'
import Separator from 'components/Separator'

import UpdateShippingInfo from '../gql/update-shipping-address.gql'
import { HeroHeader, HeroText } from '../shared-components'
import { AddressType, ShippingAddressContext } from '../ShippingAddressContext'
import { ShippingSubRoutes } from '../types'
import { formattedCorrectedAddress } from '../utils'
import { useAddressValidationTracking } from './events'
import {
  AddressBody,
  AddressCard,
  AddressCardContainer,
  AddressCardInnerContainer,
  AddressHeader,
  StyledButton,
} from './styled-components'

const INVALID_ADDRESS_OPTION = 1
const SUGGESTED_ADDRESS_OPTION = 2

export const AddressValidation = () => {
  const track = useAddressValidationTracking()
  const { getRemoteString } = useRemoteStrings()
  const { addresses, setAddress, onEdited, show } = useContext(ShippingAddressContext)
  const [selected, setSelected] = useState(SUGGESTED_ADDRESS_OPTION)
  const [submitting, setSubmitting] = useState(false)
  const [updateShippingAddress] = useMutation(UpdateShippingInfo)
  const ctaText = getRemoteString({ key: 'spend.edit_address.validation.suggested.cta' })
  const invalidHeader = getRemoteString({
    key: 'spend.edit_address.validation.suggested.invalid.header',
  })
  const suggestedHeader = getRemoteString({
    key: 'spend.edit_address.validation.suggested.suggestion.header',
  })

  const onSubmit = () => {
    switch (selected) {
      case INVALID_ADDRESS_OPTION:
        track.ctaTapped(invalidHeader, ctaText)
        show(ShippingSubRoutes.Error)
        break
      case SUGGESTED_ADDRESS_OPTION:
        track.ctaTapped(suggestedHeader, ctaText)
        if (!submitting && addresses.corrected) {
          const address = formattedCorrectedAddress(addresses.corrected)
          setSubmitting(true)
          updateShippingAddress({
            variables: address,
          }).then(() => {
            setSubmitting(false)
            onEdited()
            setAddress(AddressType.mailing, address)
            show(ShippingSubRoutes.Preview)
          })
        }
        break
    }
  }

  const unverifiedClick = () => {
    if (selected === SUGGESTED_ADDRESS_OPTION) {
      setSelected(1)
    }
  }

  const correctedClick = () => {
    if (selected === INVALID_ADDRESS_OPTION) {
      setSelected(2)
    }
  }
  useEffect(() => {
    if (!(!!addresses.unverified.street1 || !!addresses.corrected?.street1)) {
      show(ShippingSubRoutes.Edit)
    }
  }, [addresses, show])

  return (
    <Layout header={'heroText.header'} text={'heroText.text'}>
      <HeroHeader>
        {getRemoteString({ key: 'spend.edit_address.validation.suggested.header' })}
      </HeroHeader>
      <HeroText>
        {getRemoteString({ key: 'spend.edit_address.validation.suggested.body' })}
      </HeroText>
      <AddressCardContainer>
        <AddressCard onClick={unverifiedClick} selected={selected === INVALID_ADDRESS_OPTION}>
          <AddressCardInnerContainer>
            <AddressHeader>{invalidHeader}</AddressHeader>
            <AddressBody>{addresses.unverified.street1}</AddressBody>
            {addresses.unverified.street2.length > 0 && (
              <AddressBody>{addresses.unverified.street2}</AddressBody>
            )}
            <AddressBody>
              {addresses.unverified.city ? `${addresses.unverified.city},` : ''}{' '}
              {addresses.unverified.state} {addresses.unverified.zipCode}
            </AddressBody>
          </AddressCardInnerContainer>
          <AcornsIcon
            icon={
              selected === INVALID_ADDRESS_OPTION
                ? AcornsIcon.Icon.CheckmarkGreen
                : AcornsIcon.Icon.HollowCircle
            }
            width={22}
            height={22}
          />
        </AddressCard>
        {addresses.corrected && (
          <AddressCard onClick={correctedClick} selected={selected === SUGGESTED_ADDRESS_OPTION}>
            <AddressCardInnerContainer>
              <AddressHeader>{suggestedHeader}</AddressHeader>
              <AddressBody>{addresses.corrected.street1}</AddressBody>
              {addresses.corrected?.street2?.length && (
                <AddressBody>{addresses.corrected.street2}</AddressBody>
              )}
              <AddressBody>
                {addresses.corrected.city ? `${addresses.corrected.city},` : ''}{' '}
                {addresses.corrected.state} {addresses.corrected.zipCode}
              </AddressBody>
            </AddressCardInnerContainer>
            <AcornsIcon
              icon={
                selected === SUGGESTED_ADDRESS_OPTION
                  ? AcornsIcon.Icon.CheckmarkGreen
                  : AcornsIcon.Icon.HollowCircle
              }
              width={22}
              height={22}
            />
          </AddressCard>
        )}
      </AddressCardContainer>
      <Separator />
      <CTAContainer>
        <StyledButton onPress={onSubmit} disabled={submitting}>
          {submitting ? <Spinner height={20} width={20} white /> : ctaText}
        </StyledButton>
      </CTAContainer>
    </Layout>
  )
}
